/**
 * 画面詳細のタブ「コンポーネント N ／ 使われている場所 N」と、コンポーネントのタブの上に置く基本の欄の表と、
 * 使われている場所のタブの中身。
 *
 * 入った直後は「コンポーネント」のタブを見せる（画面詳細の問いは「どのコンポーネントを操作すると、何が起きるか」）。
 * URL パターン・種類・ドメインはコンポーネントのタブの中でコンポーネントの表の上に置き（Dhik 2026-09-29「画面詳細の
 * 基本欄は部品と統合して部品一覧の上に出してほしい」）、この画面へ来る所は「使われている場所」へ
 * 移した（Dhik 2026-09-29「情報をタブで分けるとかも良い」）。案件なしでも案件モードでも同じ 2 つを並べる。
 *
 * @graph-stack spec-console
 * @graph-domain Engineering
 * @graph-business 画面詳細のタブ（コンポーネント ・ 使われている場所）の並びと、コンポーネントのタブの上に置く基本の欄の表と、この画面へ来る所の並びを描く。
 * @graph-connects none
 */
import type { ScreenDetailOutput, ScreenIncoming } from '@cortex/domain-spec-console';
import { Link } from '@tanstack/react-router';
import { useTranslation } from 'react-i18next';

import type { DetailTab } from '../components/spec/DetailTabs.js';
import { TermText } from '../components/spec/TermText.js';
import { displayLabel } from '../components/spec/UntranslatedBadge.js';
import { pjLinkSearch } from './pj-link.js';
import { SCREEN_DETAIL_ROUTE } from './route-paths.js';
import { CELL_CLASS, HEAD_CELL_CLASS, InfoMark, ValueOrDash } from './screen-detail-cells.js';

/** 画面詳細のタブ。既定は `components`。 @graph-connects none */
export type ScreenDetailTab = 'components' | 'usage';

/** `screenDetailTabs` の入力。 @graph-connects none */
export interface ScreenDetailTabsInput {
  /** コンポーネントの数 */
  componentCount: number;
  /** この画面へ来る所の数 */
  incomingCount: number;
  /** 案件モードでコンポーネントのタブの中身（基本の欄かコンポーネント）が変わったか。案件なしは省く */
  componentsChanged?: boolean;
}

/**
 * 画面詳細のタブの並び。コンポーネントと使われている場所には件数を添える。
 *
 * @param input 件数と、案件モードで変わったタブ
 * @param translate 表示言語への翻訳
 * @returns タブの並び（コンポーネント ／ 使われている場所）
 * @graph-stack spec-console
 * @graph-domain Engineering
 * @graph-business 画面詳細のタブを コンポーネント（件数）・使われている場所（件数）の順に、案件モードで変わったタブに印を付けて並べる。
 * @graph-connects none
 */
export function screenDetailTabs(
  input: ScreenDetailTabsInput,
  translate: (key: string) => string,
): readonly DetailTab<ScreenDetailTab>[] {
  return [
    {
      id: 'components',
      label: translate('screenDetail.tabComponents'),
      count: input.componentCount,
      changed: input.componentsChanged,
    },
    { id: 'usage', label: translate('screenDetail.tabUsage'), count: input.incomingCount },
  ];
}

/**
 * 基本の欄の表（案件なし）。URL パターン ／ 種類 ／ ドメイン の 3 行。コンポーネントのタブの中でコンポーネントの表の上に置く。
 *
 * 種類には ⓘ を添える（「段」「タブ」が何を指すかは知らないと読み違える。この画面の ⓘ はこの 1 つ）。
 * ドメインは指すとホストを出す。
 *
 * @graph-node {Function}
 * @graph-stack spec-console
 * @graph-domain Engineering
 * @graph-business 画面の URL パターン・種類（ⓘ つき）・ドメイン（指すとホスト）を、欄名と値の 2 列の表に描く。
 * @graph-connects InfoMark [calls] 種類の ⓘ
 * @graph-connects ValueOrDash [calls] 値の無い欄の「—」
 */
export function ScreenBasicTable({ detail }: { detail: ScreenDetailOutput }) {
  const { t } = useTranslation();
  const rows = [
    {
      key: 'pathPattern',
      label: t('screenDetail.fieldPathPattern'),
      value: (
        <code className="font-mono text-xs">
          {detail.pathPattern ?? t('screenDetail.pathPatternEmpty')}
        </code>
      ),
    },
    {
      key: 'kind',
      label: t('screenDetail.fieldKind'),
      value:
        detail.kind === null ? (
          <ValueOrDash value={null} />
        ) : (
          <span data-screen-kind={detail.kind}>
            {t(`screens.kind.${detail.kind}`)}
            <InfoMark text={t(`screenDetail.kindInfo.${detail.kind}`)} />
          </span>
        ),
    },
    {
      key: 'domain',
      label: t('screenDetail.fieldDomain'),
      value: (
        <span data-screen-domain={detail.domain?.host} title={detail.domain?.host}>
          <ValueOrDash value={detail.domain?.label ?? null} />
        </span>
      ),
    },
  ];

  return (
    <table className="w-full border-collapse text-sm" data-screen-basic>
      <tbody>
        {rows.map((row) => (
          <tr key={row.key} data-field={row.key}>
            <th scope="row" className={`${HEAD_CELL_CLASS} w-40`}>
              {row.label}
            </th>
            <td className={CELL_CLASS}>{row.value}</td>
          </tr>
        ))}
      </tbody>
    </table>
  );
}

/** `ScreenIncomingList` の props。 @graph-connects none */
interface ScreenIncomingListProps {
  incoming: readonly ScreenIncoming[];
  /** リンクへ引き継ぐ案件番号。案件を選んでいなければ undefined（書き忘れを型で落とすため省略させない）。 */
  pj: string | undefined;
}

/**
 * 使われている場所のタブ。この画面へ来る所を 1 行 ＝ 来る元の画面 › コンポーネント で並べる。
 *
 * **画面だけに畳まない。** 同じ画面から 2 本入ってくるときに 1 行にすると、どちらのコンポーネントを
 * 直せばよいかが消える。コンポーネントがどの画面にも属していない行はリンクにしない（存在しない画面への
 * リンクは 404 になる）。案件を選んでいればリンクは案件を持ったまま繋ぐ。0 件ならタブの件数が
 * 「0」と言うので、中身は描かない。
 *
 * コンポーネント名は用語の番号を持たない（来る所は名前だけを返す）ので、名前の中の用語を本文と
 * 同じ規則で当ててリンクにする。
 *
 * @graph-node {Function}
 * @graph-stack spec-console
 * @graph-domain Engineering
 * @graph-business この画面へ移ってくる元を、来る元の画面（案件を引き継いだ画面詳細へのリンク）とコンポーネント（名前の中の用語は用語詳細へのリンク）の組で並べる。
 * @graph-connects TermText [calls] コンポーネント名の中の用語リンク
 * @graph-connects pjLinkSearch [calls] 案件を次の画面へ引き継ぐ検索条件
 * @graph-connects SCREEN_DETAIL_ROUTE [references] 来る元の画面詳細
 */
export function ScreenIncomingList({ incoming, pj }: ScreenIncomingListProps) {
  const { t } = useTranslation();
  if (incoming.length === 0) return null;
  return (
    <ul data-screen-incoming>
      {incoming.map((entry) => (
        <li
          key={entry.componentId}
          className="border-b border-[var(--color-border)] py-1.5 text-sm"
        >
          {entry.screenId === null ? (
            <span className="text-[var(--color-text-muted)]">
              {t('screenDetail.incomingFromUnknown')}
            </span>
          ) : (
            <Link
              to={SCREEN_DETAIL_ROUTE}
              params={{ screenId: String(entry.screenId) }}
              {...pjLinkSearch(pj)}
              className="text-[var(--color-accent-primary)] underline"
            >
              {displayLabel(entry.screenLabel, t)}
            </Link>
          )}
          <span className="mx-1.5 text-[var(--color-text-muted)]" aria-hidden="true">
            ›
          </span>
          <span data-id={entry.componentId}>
            <TermText text={displayLabel(entry.componentLabel, t)} />
          </span>
        </li>
      ))}
    </ul>
  );
}
